'use client'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { useLenis } from 'lenis/react'
import ArrowCircleLeft from 'public/icons/arrows/arrow-circle-left.svg'
import ArrowCircleRight from 'public/icons/arrows/arrow-circle-right.svg'
import Typography from 'src/components/design-system/Typography'

const DEFAULT_SCROLL_TARGET = '.list-elements'
export const SCROLL_OFFSET = -240

const SIBLINGS = 2

const buildPageNumbers = (page, pages) => {
  // first + last + 2 ellipsis + current with its siblings
  if (pages <= SIBLINGS * 2 + 5) {
    return Array.from({ length: pages }, (_, index) => index + 1)
  }

  const from = Math.max(page - SIBLINGS, 2)
  const to = Math.min(page + SIBLINGS, pages - 1)
  const numbers = [1]

  if (from > 2) {
    numbers.push('...')
  }

  for (let current = from; current <= to; current += 1) {
    numbers.push(current)
  }

  if (to < pages - 1) {
    numbers.push('...')
  }

  numbers.push(pages)

  return numbers
}

export const Pagination = ({
  page,
  pages,
  setPage,
  onPageChange,
  className = '',
  scrollTarget = DEFAULT_SCROLL_TARGET,
}) => {
  const translateEntry = useTranslateEntry()
  const lenis = useLenis()

  const totalPages = Math.max(Number(pages) || 0, 0)
  const currentPage = Math.min(Math.max(Number(page) || 1, 1), totalPages || 1)

  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages])
  const [jumpValue, setJumpValue] = useState('')
  const jumpInputId = useId()

  useEffect(() => {
    setJumpValue('')
  }, [currentPage])

  const updatePage = useCallback(
    (nextPage) => {
      const safePage = Math.min(Math.max(Number(nextPage), 1), totalPages || 1)
      setPage?.(safePage)
      onPageChange?.(safePage)

      if (!scrollTarget) return

      const target = typeof scrollTarget === 'string' ? document.querySelector(scrollTarget) : scrollTarget
      if (!target) return

      if (lenis) {
        lenis.scrollTo(target, { offset: SCROLL_OFFSET })
        return
      }

      const top = target.getBoundingClientRect().top + window.scrollY + SCROLL_OFFSET
      window.scrollTo({ top, behavior: 'smooth' })
    },
    [lenis, onPageChange, scrollTarget, setPage, totalPages],
  )

  const handleJumpSubmit = (event) => {
    event.preventDefault()

    const requestedPage = parseInt(jumpValue, 10)

    if (Number.isNaN(requestedPage)) {
      return
    }

    updatePage(requestedPage)
  }

  if (!totalPages || totalPages <= 1) {
    return null
  }

  return (
    <nav
      role="navigation"
      aria-label={translateEntry('T_PAGINATION_TITLE')}
      aria-live="off"
      className={`flex flex-wrap items-center justify-center gap-12 ${className}`}
    >
      <button
        type="button"
        onClick={() => updatePage(Number(currentPage) - 1)}
        disabled={Number(currentPage) === 1}
        className="cursor-pointer transition-opacity supports-hover:hover:opacity-80 disabled:opacity-40"
        aria-label={translateEntry('T_PAGINATION_PREV')}
      >
        <ArrowCircleLeft className="size-32 text-basic-black" aria-hidden />
      </button>

      <div className="flex items-center gap-12">
        {pageNumbers.map((pageNumber, index) =>
          pageNumber === '...' ? (
            <Typography key={`ellipsis-${index}`} variant="body3-normal" className="text-basic-black">
              ...
            </Typography>
          ) : (
            <button
              key={pageNumber}
              type="button"
              onClick={() => updatePage(Number(pageNumber))}
              aria-current={Number(pageNumber) === Number(currentPage) ? 'page' : undefined}
              className={`cursor-pointer transition-colors supports-hover:hover:text-dark-blue-dark-blue2 ${Number(currentPage) === Number(pageNumber) ? 'font-semibold text-dark-blue-dark-blue2' : 'text-basic-black'}`}
              aria-label={`${Number(pageNumber) === Number(currentPage) ? translateEntry('T_PAGINATION_CURRENT_PAGE') : translateEntry('T_PAGINATION_PAGE')} ${pageNumber}`}
            >
              <Typography variant="body3-normal">{pageNumber}</Typography>
            </button>
          ),
        )}
      </div>

      <button
        type="button"
        onClick={() => updatePage(Number(currentPage) + 1)}
        disabled={Number(currentPage) === Number(totalPages)}
        className="cursor-pointer transition-opacity supports-hover:hover:opacity-80 disabled:opacity-40"
        aria-label={translateEntry('T_PAGINATION_NEXT')}
      >
        <ArrowCircleRight className="size-32 text-basic-black" aria-hidden />
      </button>

      {totalPages > SIBLINGS * 2 + 5 && (
        <form onSubmit={handleJumpSubmit} className="flex items-center gap-8">
          <label htmlFor={jumpInputId} className="whitespace-nowrap">
            <Typography variant="body3-normal" component="span" className="text-basic-black">
              {translateEntry('T_PAGINATION_GO_TO_PAGE')}
            </Typography>
          </label>
          <input
            id={jumpInputId}
            type="number"
            inputMode="numeric"
            min={1}
            max={totalPages}
            value={jumpValue}
            onChange={(event) => setJumpValue(event.target.value)}
            placeholder={`1-${totalPages}`}
            className="h-32 w-80 rounded-4 border border-dark-blue-dark-blue6 px-8 text-basic-black"
          />
          <button
            type="submit"
            className="h-32 cursor-pointer rounded-4 border border-dark-blue-dark-blue2 px-12 text-dark-blue-dark-blue2 transition-colors supports-hover:hover:bg-dark-blue-dark-blue2 supports-hover:hover:text-basic-white"
          >
            <Typography variant="body3-normal" component="span" color="">
              {translateEntry('T_PAGINATION_GO')}
            </Typography>
          </button>
        </form>
      )}
    </nav>
  )
}
